import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { MYCHIPS_INTEGRATION_MODE_BY_PLATFORM_ALIAS } from '@mybonus/public';
import { type APIInput, type APIOutput, useAPI, useMutation, useQuery } from '@mybonus/ui';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ErrorPopup, Page } from '../../components';

type FormState = {
	platformId: number | '';
	integrationMode: 'IFRAME' | 'SDK';
	adunitId: string;
};

type CreateConfigInput = APIInput['admin']['mychips']['config']['create'];
type SupportedPlatform =
	APIOutput['admin']['mychips']['config']['list']['supportedPlatforms'][number];

function getSelectedIntegrationMode(
	platform: SupportedPlatform | undefined,
): FormState['integrationMode'] {
	return platform ? MYCHIPS_INTEGRATION_MODE_BY_PLATFORM_ALIAS[platform.alias] : 'IFRAME';
}

export function MychipsConfigCreateView() {
	const { api } = useAPI();
	const navigate = useNavigate();
	const [form, setForm] = useState<FormState>({
		platformId: '',
		integrationMode: 'IFRAME',
		adunitId: '',
	});

	const list = useQuery(
		'admin.mychips.config.list.create',
		(args) => api.admin.mychips.config.list.query(args),
		undefined,
		{ ttl: false },
	);

	const create = useMutation<CreateConfigInput>(
		(input) => api.admin.mychips.config.create.mutate(input),
		{
			onSuccess: () => {
				navigate('/app/core/mychips-config', {
					replace: true,
					state: { successText: 'MyChips config revision created' },
				});
			},
		},
	);

	const supportedPlatforms = (list.data?.supportedPlatforms || []) as SupportedPlatform[];
	const selectedPlatform = useMemo(
		() => supportedPlatforms.find((item) => item.id === form.platformId),
		[form.platformId, supportedPlatforms],
	);

	useEffect(() => {
		const firstPlatform = supportedPlatforms[0];

		if (!firstPlatform) {
			return;
		}

		setForm((current) => {
			const platformId = current.platformId || firstPlatform.id;
			const platform = supportedPlatforms.find((item) => item.id === platformId) || firstPlatform;

			return {
				...current,
				platformId,
				integrationMode: MYCHIPS_INTEGRATION_MODE_BY_PLATFORM_ALIAS[platform.alias],
			};
		});
	}, [supportedPlatforms]);

	function handlePlatformChange(platformId: number) {
		const platform = supportedPlatforms.find((item) => item.id === platformId);

		setForm((current) => ({
			...current,
			platformId,
			integrationMode: getSelectedIntegrationMode(platform),
		}));
	}

	function handleCreate() {
		if (!form.platformId || form.adunitId.trim().length === 0) {
			return;
		}

		create.mutate({
			platformId: form.platformId,
			integrationMode: form.integrationMode,
			adunitId: form.adunitId.trim(),
		});
	}

	const error = list.error || create.error;
	const isLoading = list.isLoading || create.isLoading;
	const selectedIntegrationMode = getSelectedIntegrationMode(selectedPlatform);

	return (
		<Page title="New MyChips Revision" width="contained">
			<ErrorPopup
				error={error}
				onRetry={list.load}
				onClose={() => {
					list.reset();
					create.reset();
				}}
			/>

			{isLoading && <LinearProgress />}

			<Paper sx={{ p: 3 }}>
				<Stack spacing={2}>
					<Typography variant="body2">
						Create a new inactive revision, then return to the config list to preview or activate
						it.
					</Typography>

					<TextField
						select
						size="small"
						label="Platform"
						value={form.platformId}
						onChange={(e) => handlePlatformChange(Number(e.target.value))}
					>
						{supportedPlatforms.map((platform: SupportedPlatform) => (
							<MenuItem key={platform.id} value={platform.id}>
								{platform.alias}
							</MenuItem>
						))}
					</TextField>

					<TextField
						select
						size="small"
						label="Integration mode"
						value={form.integrationMode}
						onChange={(e) =>
							setForm((current) => ({
								...current,
								integrationMode: e.target.value as FormState['integrationMode'],
							}))
						}
					>
						<MenuItem value={selectedIntegrationMode}>{selectedIntegrationMode}</MenuItem>
					</TextField>

					<TextField
						size="small"
						label="Ad unit id"
						value={form.adunitId}
						onChange={(e) => setForm((current) => ({ ...current, adunitId: e.target.value }))}
					/>

					<Stack direction="row" spacing={1}>
						<Button variant="contained" disabled={create.isLoading} onClick={handleCreate}>
							Create inactive revision
						</Button>
						<Button
							color="inherit"
							disabled={create.isLoading}
							onClick={() => navigate('/app/core/mychips-config')}
						>
							Cancel
						</Button>
					</Stack>
				</Stack>
			</Paper>
		</Page>
	);
}
